import { useViewingContext } from "@/shared/services/ViewingContextService";

export interface PanelLabel {
  /** How to name the panel, e.g. "Plot · Velocity". Absent when unknown. */
  label?: string;
  /**
   * The workspace is open and has no panel with this id — the call will fail.
   *
   * False whenever the workspace is not open, which is a different situation:
   * `execute` refuses those with "the visualizer is not open", and claiming
   * the panel is gone would be guessing.
   */
  knownMissing: boolean;
}

/** The visualizer's panel map, as far as its untyped state can be trusted. */
function panelsFromState(
  state: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  const panels = state?.panels;
  return typeof panels === "object" && panels !== null && !Array.isArray(panels)
    ? (panels as Record<string, unknown>)
    : undefined;
}

function describePanel(panel: unknown): string | undefined {
  if (typeof panel !== "object" || panel === null) {
    return undefined;
  }
  const { type, title } = panel as { type?: unknown; title?: unknown };
  const panelType = typeof type === "string" ? type : undefined;
  const panelTitle =
    typeof title === "string" && title !== "" ? title : undefined;

  if (panelType !== undefined && panelTitle !== undefined) {
    return `${panelType} · ${panelTitle}`;
  }
  return panelTitle ?? panelType;
}

/**
 * Name the panel a tool proposes to change, from the workspace on screen.
 *
 * No fetch: the visualizer registers its state in the viewing context, and
 * the chat sits beside it. That also makes the panel's absence knowable —
 * the model picks ids out of a context snapshot that can be stale, so an id
 * the workspace no longer has is a call worth refusing before it runs.
 */
export function usePanelLabel(panelId: string | undefined): PanelLabel {
  const viewingContext = useViewingContext();
  const panels = panelsFromState(viewingContext.visualizer?.state);

  if (panelId === undefined || panels === undefined) {
    return { knownMissing: false };
  }

  if (!(panelId in panels)) {
    return { knownMissing: true };
  }

  return { label: describePanel(panels[panelId]), knownMissing: false };
}
